import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { createEAProject } from '../services/ea-api'
import type { EAEngagementType, EAProjectCreate as EAProjectCreateType } from '../types/ea'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const inputStyle = {
  background: 'rgba(30, 41, 59, 0.5)',
  border: '1px solid rgba(100, 180, 255, 0.15)',
  color: '#e2e8f0',
}

const VENDOR_OPTIONS = ['Arista', 'PaloAlto', 'Nutanix', 'Azure', 'Cisco']

const VENDOR_COLORS: Record<string, string> = {
  Arista: '#2563eb',
  PaloAlto: '#dc2626',
  Nutanix: '#059669',
  Azure: '#0284c7',
  Cisco: '#7c3aed',
}

const ENGAGEMENT_TYPES: { value: EAEngagementType; label: string }[] = [
  { value: 'consulting', label: 'Consulting' },
  { value: 'migration', label: 'Migration' },
  { value: 'greenfield', label: 'Greenfield' },
  { value: 'refresh', label: 'Refresh' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'rfp_response', label: 'RFP Response' },
]

export default function EAProjectCreate() {
  const navigate = useNavigate()
  const { accounts } = useMsal()
  const currentUser = accounts[0]?.username || ''

  const [formData, setFormData] = useState({
    client_name: '',
    project_name: '',
    engagement_type: 'consulting' as EAEngagementType,
    vendors: [] as string[],
    project_description: '',
    project_lead: '',
    sow_number: '',
    target_completion: '',
    tags: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useMutation({
    mutationFn: (data: EAProjectCreateType) => createEAProject(data),
    onSuccess: (response) => {
      const newProject = response.data
      navigate(`/ea/projects/${newProject.id}`)
    },
  })

  const handleVendorToggle = (vendor: string) => {
    setFormData((prev) => ({
      ...prev,
      vendors: prev.vendors.includes(vendor)
        ? prev.vendors.filter((v) => v !== vendor)
        : [...prev.vendors, vendor],
    }))
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.client_name.trim()) newErrors.client_name = 'Client name is required'
    if (!formData.project_name.trim()) newErrors.project_name = 'Project name is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const payload: EAProjectCreateType = {
      client_name: formData.client_name.trim(),
      project_name: formData.project_name.trim(),
      engagement_type: formData.engagement_type,
      created_by: currentUser,
      vendors: formData.vendors.length > 0 ? formData.vendors : undefined,
      project_description: formData.project_description.trim() || undefined,
      project_lead: formData.project_lead.trim() || undefined,
      sow_number: formData.sow_number.trim() || undefined,
      target_completion: formData.target_completion || undefined,
      tags: formData.tags.trim()
        ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : undefined,
    }

    createMutation.mutate(payload)
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/ea/projects"
          className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
          style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(100, 180, 255, 0.15)',
            color: 'rgba(148, 163, 184, 0.8)',
          }}
        >
          <i className="fa-solid fa-arrow-left"></i>
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>New EA Project</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Create a new enterprise architecture engagement
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="rounded-2xl p-8" style={glassPanel}>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Client Name */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                Client Name <span style={{ color: '#f87171' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Acme Corporation"
                required
                value={formData.client_name}
                onChange={(e) => { setFormData({ ...formData, client_name: e.target.value }); setErrors({ ...errors, client_name: '' }) }}
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={inputStyle}
              />
              {errors.client_name && (
                <p className="text-xs mt-1" style={{ color: '#f87171' }}>{errors.client_name}</p>
              )}
              <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                The client organization for this engagement
              </p>
            </div>

            {/* Project Name */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                Project Name <span style={{ color: '#f87171' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Data Center Refresh 2026"
                required
                value={formData.project_name}
                onChange={(e) => { setFormData({ ...formData, project_name: e.target.value }); setErrors({ ...errors, project_name: '' }) }}
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={inputStyle}
              />
              {errors.project_name && (
                <p className="text-xs mt-1" style={{ color: '#f87171' }}>{errors.project_name}</p>
              )}
              <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                A descriptive name for this project
              </p>
            </div>

            {/* Engagement Type */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                Engagement Type
              </label>
              <select
                value={formData.engagement_type}
                onChange={(e) => setFormData({ ...formData, engagement_type: e.target.value as EAEngagementType })}
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={inputStyle}
              >
                {ENGAGEMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                The type of enterprise architecture engagement
              </p>
            </div>

            {/* Vendors */}
            <div>
              <label className="block text-sm font-semibold mb-3" style={{ color: '#e2e8f0' }}>
                Vendors
              </label>
              <div className="flex flex-wrap gap-3">
                {VENDOR_OPTIONS.map((vendor) => {
                  const isSelected = formData.vendors.includes(vendor)
                  const color = VENDOR_COLORS[vendor]
                  return (
                    <button
                      key={vendor}
                      type="button"
                      onClick={() => handleVendorToggle(vendor)}
                      className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2"
                      style={{
                        background: isSelected ? `${color}33` : 'rgba(30, 41, 59, 0.5)',
                        border: `1px solid ${isSelected ? `${color}66` : 'rgba(100, 180, 255, 0.15)'}`,
                        color: isSelected ? color : 'rgba(148, 163, 184, 0.7)',
                      }}
                    >
                      <i className={`fa-solid ${isSelected ? 'fa-check-square' : 'fa-square'}`}></i>
                      <span>{vendor}</span>
                    </button>
                  )
                })}
              </div>
              <p className="text-xs mt-2" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                Select the vendors involved in this project
              </p>
            </div>

            {/* Project Description */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                Project Description
              </label>
              <textarea
                rows={4}
                placeholder="Brief description of the engagement scope and objectives..."
                value={formData.project_description}
                onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                className="w-full px-4 py-3 rounded-xl text-sm resize-none"
                style={inputStyle}
              ></textarea>
            </div>

            {/* Two-column row: Project Lead + SOW Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                  Project Lead
                </label>
                <input
                  type="text"
                  placeholder="jane.doe@coretek.com"
                  value={formData.project_lead}
                  onChange={(e) => setFormData({ ...formData, project_lead: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                  SOW Number
                </label>
                <input
                  type="text"
                  placeholder="SOW-2026-001"
                  value={formData.sow_number}
                  onChange={(e) => setFormData({ ...formData, sow_number: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Two-column row: Target Completion + Tags */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                  Target Completion
                </label>
                <input
                  type="date"
                  value={formData.target_completion}
                  onChange={(e) => setFormData({ ...formData, target_completion: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                  Tags
                </label>
                <input
                  type="text"
                  placeholder="dc-refresh, priority, q2-2026"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm"
                  style={inputStyle}
                />
                <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Comma-separated tags for organization
                </p>
              </div>
            </div>

            {/* Info box */}
            <div
              className="rounded-xl p-4"
              style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
            >
              <div className="flex space-x-3">
                <i className="fa-solid fa-info-circle mt-0.5" style={{ color: '#60a5fa' }}></i>
                <div>
                  <h4 className="text-sm font-semibold mb-1" style={{ color: '#60a5fa' }}>
                    What happens next?
                  </h4>
                  <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                    After creating the project, the EA-Orchestrator will initialize the pipeline with
                    discovery tasks for each selected vendor. You can then run vendor pipelines,
                    generate architecture artifacts, and promote deliverables.
                  </p>
                </div>
              </div>
            </div>

            {/* Error display */}
            {createMutation.isError && (
              <div
                className="rounded-xl p-4"
                style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
              >
                <div className="flex space-x-3">
                  <i className="fa-solid fa-exclamation-triangle mt-0.5" style={{ color: '#ef4444' }}></i>
                  <div>
                    <h4 className="text-sm font-semibold mb-1" style={{ color: '#ef4444' }}>
                      Failed to create project
                    </h4>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                      {(createMutation.error as any)?.response?.data?.detail || 'An unexpected error occurred. Please try again.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div
            className="flex items-center justify-end space-x-3 mt-8 pt-6"
            style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}
          >
            <Link
              to="/ea/projects"
              className="px-6 py-3 rounded-xl text-sm font-medium transition"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: 'rgba(148, 163, 184, 0.9)',
              }}
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                border: '1px solid rgba(96, 165, 250, 0.4)',
                color: '#e2e8f0',
              }}
            >
              {createMutation.isPending ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-plus"></i>
                  <span>Create Project</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
